import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Minus, Plus, RotateCcw, ShieldCheck, Truck } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useStore } from "@/context/StoreContext";
import { api, errMsg, firstAvailable, inr } from "@/lib/api";
import { Gallery } from "@/components/Gallery";
import { AddToBagButton, Cta } from "@/components/Cta";
import { Availability, Price, VariantChips, WishButton } from "@/components/ProductBits";
import { ProductCard } from "@/components/ProductCard";

const SWATCH = { gold: "#C9A46A", silver: "#C8C8CC", "rose gold": "#D9A38E", "ruby red": "#A9344D", ruby: "#A9344D", emerald: "#276653", "emerald green": "#276653", "sapphire blue": "#344E82", sapphire: "#344E82", pearl: "#F2EDE4" };

const Info = ({ p, site }) => (
  <Accordion type="multiple" defaultValue={["details"]} className="border-t border-plum/10">
    {[
      ["details", "Product details", (
        <div className="space-y-4">
          <p>{p.description}</p>
          {p.specs?.length > 0 && <dl className="space-y-2">{p.specs.map((s) => <div key={s.label} className="grid grid-cols-[130px_1fr] gap-3"><dt className="font-bold">{s.label}</dt><dd>{s.value}</dd></div>)}</dl>}
          {p.materials && <p><span className="font-bold">Materials: </span>{p.materials}</p>}
        </div>
      )],
      ["shipping", "Shipping information", site.policies.shipping],
      ["returns", "Returns", site.policies.returns],
      ["care", "Jewellery care", site.policies.care],
    ].map(([k, t, body]) => (
      <AccordionItem key={k} value={k} className="border-plum/10">
        <AccordionTrigger data-testid={`pdp-accordion-${k}`} className="font-display text-[24px] font-semibold hover:no-underline py-5">{t}</AccordionTrigger>
        <AccordionContent className="text-[16px] leading-relaxed text-plum/80 pb-6">{body}</AccordionContent>
      </AccordionItem>
    ))}
  </Accordion>
);

export default function ProductPage() {
  const { slug } = useParams();
  const { products, addToBag, site } = useStore();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [err, setErr] = useState("");
  const [variant, setVariant] = useState("");
  const [qty, setQty] = useState(1);
  const [buying, setBuying] = useState(false);

  useEffect(() => {
    setP(null);
    setErr("");
    window.scrollTo(0, 0);
    api.get(`/products/${slug}`).then((r) => { setP(r.data); setVariant(firstAvailable(r.data)); setQty(1); }).catch((e) => setErr(errMsg(e)));
  }, [slug]);

  if (err) return <div className="container-rh py-32 text-center"><p className="font-display text-[40px]">{err}</p><div className="mt-8"><Cta to="/shop" label="See all pieces" /></div></div>;
  if (!p) return <div className="container-rh py-10 grid lg:grid-cols-12 gap-10"><div className="lg:col-span-7 aspect-[4/5] rounded-[24px] bg-blush animate-pulse" /><div className="lg:col-span-5 space-y-4"><div className="h-12 rounded-xl bg-blush animate-pulse" /><div className="h-8 w-1/2 rounded-xl bg-blush animate-pulse" /></div></div>;

  const stock = p.variants.length ? p.variants.find((v) => v.label === variant)?.stock ?? 0 : p.available;
  const soldOut = stock <= 0;
  const buy = async () => {
    setBuying(true);
    try {
      await addToBag(p, variant, qty, { silent: true });
      nav("/checkout");
    } catch {
      setBuying(false);
    }
  };
  const related = products.filter((x) => x.id !== p.id).slice(0, 3);
  const free = site.shipping.free_threshold;

  return (
    <main data-testid="product-page" className="pb-28 lg:pb-0">
      <div className="container-rh pt-4 lg:pt-8">
        <nav className="hidden sm:flex text-[13px] font-semibold text-plum/60 gap-2 mb-6">
          <Link to="/" className="hover:text-plum">Home</Link>/<Link to="/shop" className="hover:text-plum">The Edit</Link>/<span className="text-plum">{p.name}</span>
        </nav>
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-14">
          <div className="lg:col-span-7"><Gallery images={p.images} name={p.name} /></div>
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-[132px] space-y-6">
              <div>
                <p className="eyebrow text-berry">{p.label ? `${p.label} · ` : ""}{p.type}</p>
                <h1 className="font-display font-medium text-[40px] sm:text-[48px] lg:text-[54px] leading-[1.02] mt-3" data-testid="pdp-name">{p.name}</h1>
                <p className="text-[17px] text-plum/75 mt-3 leading-relaxed">{p.tagline}</p>
              </div>
              <div className="space-y-2"><Price p={p} size="lg" /><p className="text-[13px] text-plum/60">Inclusive of all taxes</p></div>
              {p.colours?.length > 0 && (
                <div>
                  <p className="text-[14px] font-bold mb-2.5">Colour: <span className="font-medium">{p.colours.join(" & ")}</span></p>
                  <div className="flex gap-2">{p.colours.map((c) => <span key={c} title={c} className="w-8 h-8 rounded-full ring-1 ring-plum/20 ring-offset-2 ring-offset-pearl" style={{ background: SWATCH[c.toLowerCase()] || "#D9D2CB" }} />)}</div>
                </div>
              )}
              {p.variants.length > 0 && (
                <div>
                  <p className="text-[14px] font-bold mb-2.5">{p.variant_name}: <span className="font-medium">{variant}</span></p>
                  <VariantChips p={p} value={variant} onChange={(v) => { setVariant(v); setQty(1); }} size="lg" />
                </div>
              )}
              <div className="flex items-center gap-5 flex-wrap">
                <div className="flex items-center rounded-[14px] border border-plum/20 bg-white">
                  <button onClick={() => setQty(Math.max(1, qty - 1))} aria-label="Decrease quantity" data-testid="pdp-qty-minus" className="w-12 h-12 grid place-items-center"><Minus className="w-4 h-4" /></button>
                  <span className="w-8 text-center font-extrabold text-[17px]" data-testid="pdp-qty">{qty}</span>
                  <button onClick={() => setQty(Math.min(Math.min(10, stock), qty + 1))} aria-label="Increase quantity" data-testid="pdp-qty-plus" className="w-12 h-12 grid place-items-center"><Plus className="w-4 h-4" /></button>
                </div>
                <Availability p={p} variant={variant} />
              </div>
              <div className="space-y-3">
                <Cta full size="lg" variant="berry" label={soldOut ? "Sold out" : "Buy this piece"} disabled={soldOut} state={buying ? "loading" : "idle"} onClick={buy} testId="pdp-buy-now" />
                <div className="flex gap-3">
                  <AddToBagButton full className="flex-1" testId="pdp-add-to-bag" disabled={soldOut} onAdd={() => addToBag(p, variant, qty)} />
                  <WishButton p={p} className="!w-[54px] !h-[54px] border border-plum/15 flex-none" />
                </div>
              </div>
              <ul className="grid grid-cols-3 gap-2 text-[13px] font-semibold text-plum/80">
                <li className="rounded-[14px] bg-blush p-3 flex flex-col gap-2"><Truck className="w-5 h-5 text-berry" />{free > 0 ? `Free shipping over ${inr(free)}` : "Free shipping"}</li>
                <li className="rounded-[14px] bg-lilac p-3 flex flex-col gap-2"><RotateCcw className="w-5 h-5 text-berry" />Easy returns</li>
                <li className="rounded-[14px] bg-[#EAF2EE] p-3 flex flex-col gap-2"><ShieldCheck className="w-5 h-5 text-emerald" />Secure UPI payment</li>
              </ul>
            </div>
          </div>
        </div>

        <section className="grid lg:grid-cols-12 gap-8 mt-20 lg:mt-28">
          <div className="lg:col-span-4"><h2 className="font-display text-[40px] lg:text-[48px] leading-none font-medium">The details</h2><p className="text-plum/70 mt-3">{p.story}</p></div>
          <div className="lg:col-span-8"><Info p={p} site={site} /></div>
        </section>

        {related.length > 0 && (
          <section className="mt-20 lg:mt-28 pb-20">
            <h2 className="font-display text-[40px] lg:text-[48px] font-medium mb-10">Complete the edit</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-14">{related.map((r) => <ProductCard key={r.id} p={r} index={products.indexOf(r)} />)}</div>
          </section>
        )}
      </div>

      <div className="lg:hidden fixed bottom-0 inset-x-0 z-30 bg-ivory/95 backdrop-blur-xl border-t border-plum/10 px-3 py-3 flex items-center gap-3" data-testid="pdp-sticky-bar">
        <div className="min-w-0 pl-1">
          <p className="text-[12px] font-semibold text-plum/70 truncate max-w-[120px]">{variant || p.type}</p>
          <p className="font-extrabold text-[18px]">{inr(p.effective_price * qty)}</p>
        </div>
        <Cta className="flex-1" size="md" variant="berry" label={soldOut ? "Sold out" : "Buy this piece"} disabled={soldOut} state={buying ? "loading" : "idle"} onClick={buy} testId="pdp-sticky-buy" />
      </div>
    </main>
  );
}
